import type { Job as TJob } from "@eai-toolkit/client";
import { Box } from "@mui/material";
import { tracker } from "@openreplay/tracker";
import { useCallback, useContext } from "react";
import type { MouseEvent } from "react";

import { getJobDetailUrl, JOBS_ROUTE } from "@helpers/constants";
import { DASHBOARD_EVENTS } from "@helpers/events";
import { formatDateTimeString, formatDistance } from "@helpers/utils";

import renderer from "../../../data/defaultFieldRenderer";
import { default as stateRenderer } from "../../../data/fieldRenderers/JobState";
import { useRowNavigation } from "../../../hooks/useRowNavigation";
import { GetStartedContext } from "../../app/components/GetStarted/GetStartedContext";
import { DataTable, type RequestState } from "./DataTable";

interface JobsTableProps {
  jobs: TJob[];
  onCancelJob?: (id: string) => void;
  onRetryJob?: (id: string) => void;
  hasJobSetPermission?: (job: TJob) => boolean;
  requestState: RequestState;
  error: string | null;
}

const getJobDetailPath = (job: TJob) => getJobDetailUrl(job.id);

export const JobsTable = ({ jobs, requestState, error }: JobsTableProps) => {
  const { toggleDrawer } = useContext(GetStartedContext);
  const baseHandleRowClick = useRowNavigation<TJob>(getJobDetailPath);
  const handleRowClick = useCallback(
    (job: TJob, event?: MouseEvent<Element>) => {
      tracker.event(DASHBOARD_EVENTS.JOB_ROW_CLICKED);
      baseHandleRowClick(job, event);
    },
    [baseHandleRowClick],
  );

  const columns = [
    {
      header: "Name",
      accessor: (job: TJob) => renderer(job.name ?? job.id).node,
    },
    {
      header: "Submitted by",
      accessor: (job: TJob) => renderer(job.creator).node,
      padding: "none" as const,
    },
    {
      header: "State",
      accessor: (job: TJob) => stateRenderer(job.state).node,
      width: "120px",
    },
    {
      header: "Created",
      accessor: (job: TJob) =>
        renderer(formatDistance(job.created), formatDateTimeString(job.created))
          .node,
    },
  ];

  const emptyStateMessage = (
    <>
      No jobs found. Try a different filter or go to the{" "}
      <Box component="a" href={JOBS_ROUTE} sx={{ color: "primary.main" }}>
        Jobs page
      </Box>{" "}
      to refine your search.
      <br />
      New to AI Toolkit?{" "}
      <Box
        component="a"
        href="#"
        onClick={() => toggleDrawer()}
        sx={{ color: "primary.main" }}
      >
        Get started
      </Box>
      .
    </>
  );

  return (
    <DataTable
      items={jobs}
      columns={columns}
      requestState={requestState}
      error={error}
      emptyStateMessage={emptyStateMessage}
      seeAllLink={JOBS_ROUTE}
      onRowClick={handleRowClick}
    />
  );
};
